import { useState, useEffect } from 'react';
import { getPLData, savePLData } from '../../services/plDataService';
import toast from 'react-hot-toast';

function DataAnalysis() {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    revenue: {
      recurring: 120000,
      new: 80000,
      total: 200000
    },
    costs: {
      fixed: 50000,
      variable: 100000,
      total: 150000
    },
    profitability: {
      grossMargin: 0.45,
      netProfit: 0.25
    }
  });

  const [categories, setCategories] = useState({
    marketing: 30000,
    operations: 40000,
    discounts: 15000,
    returns: 5000
  });

  useEffect(() => {
    fetchPLData();
  }, []);

  const fetchPLData = async () => {
    try {
      setLoading(true);
      const data = await getPLData('current-user-id'); // Replace with actual user ID
      if (data.length > 0) {
        const latestData = data[0];
        setMetrics({
          revenue: latestData.revenue,
          costs: latestData.costs,
          profitability: latestData.profitability
        });
        setCategories(latestData.categories);
      }
    } catch (error) {
      toast.error('Failed to load P&L data');
      console.error('Error loading P&L data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0049ac]"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Data Analysis</h1>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Recurring Revenue</span>
                  <span className="font-medium">${metrics.revenue.recurring.toLocaleString()}</span>
                </div>
                <div className="mt-1 h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-green-500 rounded-full"
                    style={{ width: `${(metrics.revenue.recurring / metrics.revenue.total) * 100}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">New Customer Revenue</span>
                  <span className="font-medium">${metrics.revenue.new.toLocaleString()}</span>
                </div>
                <div className="mt-1 h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-blue-500 rounded-full"
                    style={{ width: `${(metrics.revenue.new / metrics.revenue.total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Costs</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Fixed Costs</span>
                  <span className="font-medium">${metrics.costs.fixed.toLocaleString()}</span>
                </div>
                <div className="mt-1 h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-red-500 rounded-full"
                    style={{ width: `${(metrics.costs.fixed / metrics.costs.total) * 100}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Variable Costs</span>
                  <span className="font-medium">${metrics.costs.variable.toLocaleString()}</span>
                </div>
                <div className="mt-1 h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-orange-500 rounded-full"
                    style={{ width: `${(metrics.costs.variable / metrics.costs.total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Profitability</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Gross Margin</span>
                  <span className="font-medium">{(metrics.profitability.grossMargin * 100).toFixed(1)}%</span>
                </div>
                <div className="mt-1 h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-purple-500 rounded-full"
                    style={{ width: `${metrics.profitability.grossMargin * 100}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Net Profit</span>
                  <span className="font-medium">{(metrics.profitability.netProfit * 100).toFixed(1)}%</span>
                </div>
                <div className="mt-1 h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-indigo-500 rounded-full"
                    style={{ width: `${metrics.profitability.netProfit * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Analysis */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Category Analysis</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {Object.entries(categories).map(([category, amount]) => (
              <div key={category} className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 capitalize">{category}</h3>
                <p className="mt-2 text-2xl font-semibold text-gray-900">${amount.toLocaleString()}</p>
                <div className="mt-2 text-sm text-gray-500">
                  {((amount / metrics.costs.total) * 100).toFixed(1)}% of total costs
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataAnalysis;